import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BsCopy, BsEye, BsPencil, BsPlusLg, BsSend, BsTrash } from "react-icons/bs";
import { BiSearch } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { usePagination, useTable } from "react-table";
import Loader from "../../components/Loader";
import Select from "../../components/Select";
import Api from "../../service/http";

const Alerts = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [viewMessage, setViewMessage] = useState(null);

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const fetchAlerts = async (searchTerm = '', page = 1) => {
    setLoading(true);
    try {
      const response = await Api.get(`web/alerts?page=${page}&search=${searchTerm}`);
      const data = response.data;
      setAlerts(data?.data);
      setPageCount(data?.last_page || 0);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useCallback(debounce((value, page) => {
    fetchAlerts(value, page);
  }, 500), []);

  useEffect(() => {
    debouncedFetch(search, pagination.pageIndex + 1);
  }, [search, pagination.pageIndex]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleDelete = async () => {
    try {
      await Api.delete(`web/alerts/${deleteId}`);
      alert("Alert deleted successfully!");
      setDeleteId(null);
      setShowConfirm(false);
      fetchAlerts(search, pagination.pageIndex + 1);
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const sendNotification = async () => {
    if (!modalContent) return;

    setModalLoading(true);
    try {
      await Api.post("web/send-notification", modalContent);
      alert("Notification sent successfully!");
      setShowModal(false);
      setModalContent(null);
      fetchAlerts(search, pagination.pageIndex + 1);
    } catch (error) {
      console.error("Error sending notification:", error);
    }
    setModalLoading(false);
  };

  const columns = useMemo(() => [
    {
      Header: "Status", accessor: "status", Cell: ({ value }) => (
        <span className="text-sm font-medium">
          {value === "published" ? (
            <span className="bg-green-50 text-green-700 py-1 px-3 rounded-full text-xs border border-green-100">Published</span>
          ) : (
            <span className="bg-amber-50 text-amber-700 py-1 px-3 rounded-full text-xs border border-amber-100">Draft</span>
          )}
        </span>
      )
    },
    { Header: "Message", accessor: "message", Cell: ({ value }) => <span className="text-sm text-gray-600 truncate max-w-[200px] block" title={value}>{value}</span> },
    { Header: "Created At", accessor: "created_at", Cell: ({ value }) => <span className="text-sm text-gray-500">{new Date(value).toLocaleString()}</span> },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <button
            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={() => setViewMessage(row.original.message)}
            title="View Full Message"
          >
            <BsEye className="text-lg" />
          </button>
          {row.original.status === "published" && (
            <button
              className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              onClick={() => {
                setShowModal(true);
                setModalContent({ ...row?.original, type: 'public', category: 'active_subscribers' });
              }}
              title="Send Notification"
            >
              <BsSend className="text-lg" />
            </button>
          )}
          {row.original.status === "draft" && (
            <button
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              onClick={() => navigate(`/alerts/edit/${row.original.id}`)}
              title="Edit Alert"
            >
              <BsPencil className="text-lg" />
            </button>
          )}
          <button
            className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
            onClick={() => {
              navigator.clipboard.writeText("https://itsallaboutheoptions.com/alerts?code=" + row.original.code);
              alert("Alert code copied to clipboard!");
            }}
            title="Copy Alert Code"
          >
            <BsCopy className="text-lg" />
          </button>
          <button
            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            onClick={() => {
              setDeleteId(row.original.id);
              setShowConfirm(true);
            }}
            title="Delete Alert"
          >
            <BsTrash className="text-lg" />
          </button>
        </div>
      ),
    },
  ], [navigate]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount: tablePageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: alerts,
      initialState: { pageIndex: pagination.pageIndex, pageSize: pagination.pageSize },
      manualPagination: true,
      pageCount,
    },
    usePagination
  );

  useEffect(() => {
    setPagination({ pageIndex, pageSize });
  }, [pageIndex, pageSize]);

  return (
    <div className="p-0 h-full overflow-y-auto scrollbar-hide bg-gray-50/50">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Alerts</h1>
            <p className="text-gray-500 text-sm mt-1">Manage and send push notifications to your subscribers.</p>
          </div>
          <button
            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-xl font-semibold text-sm hover:bg-indigo-700 transition-all shadow-md shadow-indigo-100"
            onClick={() => navigate("/alerts/add")}
          >
            <BsPlusLg />
            Add New Alert
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50/30">
            <div className="relative">
              <BiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
              <input
                type="text"
                placeholder="Search alerts..."
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
                value={search}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          <div className="overflow-x-auto hidden md:block">
            <table {...getTableProps()} className="w-full">
              <thead>
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()} className="bg-gray-50/50">
                    {headerGroup.headers.map(column => (
                      <th
                        {...column.getHeaderProps()}
                        className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100"
                      >
                        {column.render('Header')}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} className="py-20">
                      <Loader size="lg" className="mx-auto" />
                    </td>
                  </tr>
                ) : page?.length > 0 ? (
                  page.map(row => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} className="hover:bg-gray-50/50 transition-colors">
                        {row.cells.map(cell => (
                          <td
                            {...cell.getCellProps()}
                            className="px-6 py-4 text-sm text-gray-600"
                          >
                            {cell.render('Cell')}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                          <BiSearch className="text-2xl text-gray-300" />
                        </div>
                        <p>No alerts found matching your criteria</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="block md:hidden">
            {loading ? (
              <div className="py-20">
                <Loader size="lg" className="mx-auto" />
              </div>
            ) : page?.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {page.map(row => {
                  prepareRow(row);
                  const { original } = row;
                  return (
                    <div key={original.id} className="p-4 space-y-3 hover:bg-gray-50/50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex flex-col gap-1">
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Status</span>
                          {original.status === "published" ? (
                            <span className="bg-green-50 text-green-700 py-0.5 px-2.5 rounded-full text-[10px] border border-green-100 w-fit font-medium">Published</span>
                          ) : (
                            <span className="bg-amber-50 text-amber-700 py-0.5 px-2.5 rounded-full text-[10px] border border-amber-100 w-fit font-medium">Draft</span>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 items-end">
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Created At</span>
                          <span className="text-xs text-gray-500">{new Date(original.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Message</span>
                        <p className="text-sm text-gray-700 line-clamp-3 leading-relaxed">
                          {original.message}
                        </p>
                      </div>

                      <div className="flex items-center justify-between pt-2">
                        <div className="flex items-center gap-2">
                          <button
                            className="p-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
                            onClick={() => setViewMessage(original.message)}
                            title="View"
                          >
                            <BsEye className="text-lg" />
                          </button>
                          {original.status === "published" && (
                            <button
                              className="p-2 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-all"
                              onClick={() => {
                                setShowModal(true);
                                setModalContent({ ...original, type: 'public', category: 'active_subscribers' });
                              }}
                              title="Send"
                            >
                              <BsSend className="text-lg" />
                            </button>
                          )}
                          {original.status === "draft" && (
                            <button
                              className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all"
                              onClick={() => navigate(`/alerts/edit/${original.id}`)}
                              title="Edit"
                            >
                              <BsPencil className="text-lg" />
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            className="p-2 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-all"
                            onClick={() => {
                              navigator.clipboard.writeText("https://itsallaboutheoptions.com/alerts?code=" + original.code);
                              alert("Alert code copied to clipboard!");
                            }}
                            title="Copy"
                          >
                            <BsCopy className="text-lg" />
                          </button>
                          <button
                            className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-all"
                            onClick={() => {
                              setDeleteId(original.id);
                              setShowConfirm(true);
                            }}
                            title="Delete"
                          >
                            <BsTrash className="text-lg" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="px-6 py-12 text-center text-gray-500">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                    <BiSearch className="text-2xl text-gray-300" />
                  </div>
                  <p className="text-sm">No alerts found</p>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/30">
            <div className="text-sm text-gray-500 font-medium">
              Showing page <span className="text-gray-900">{pageIndex + 1}</span> of <span className="text-gray-900">{pageOptions.length}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all shadow-sm"
              >
                Previous
              </button>
              <button
                onClick={() => nextPage()}
                disabled={!canNextPage}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all shadow-sm"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {viewMessage && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden transform transition-all">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Message Content</h3>
              <button 
                onClick={() => setViewMessage(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="bg-gray-50 rounded-xl p-4 text-gray-700 text-sm leading-relaxed max-h-[60vh] overflow-y-auto">
                {viewMessage}
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  className="px-6 py-2 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-all text-sm"
                  onClick={() => setViewMessage(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showConfirm && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden transform transition-all">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <BsTrash className="text-2xl text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Alert</h3>
              <p className="text-gray-500 text-sm">
                Are you sure you want to delete this alert? This action cannot be undone.
              </p>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex flex-col-reverse sm:flex-row gap-3">
              <button
                className="flex-1 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-100 transition-all text-sm"
                onClick={() => {
                  setDeleteId(null);
                  setShowConfirm(false);
                }}
              >
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-2.5 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all text-sm shadow-md shadow-red-100"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden transform transition-all">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Send Alert</h3>
              <button 
                onClick={() => {
                  setShowModal(false);
                  setModalContent(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-gray-500 mb-5">
                Manually send alert <span className="font-bold text-red-600 px-1.5 py-0.5 bg-red-50 rounded border border-red-100">{modalContent?.send_count}</span> times
              </p>
              
              <div className="space-y-4">
                <Select
                  label="Audience Type"
                  value={modalContent?.type}
                  onChange={(e) => {
                    setModalContent((prev) => ({
                      ...prev,
                      type: e.target.value,
                    }));
                  }}
                  options={[
                    { label: "Public", value: "public" },
                    { label: "Private", value: "private" }
                  ]}
                  placeholder={null}
                />

                {modalContent?.type === 'private' && (
                  <Select
                    label="Audience Category"
                    value={modalContent?.category}
                    onChange={(e) => {
                      setModalContent((prev) => ({
                        ...prev,
                        category: e.target.value,
                      }));
                    }}
                    options={[
                      { label: "Active Subscribers", value: "active_subscribers" },
                      { label: "Inactive Subscribers", value: "inactive_subscribers" },
                      { label: "All Subscribers", value: "all_subscribers" }
                    ]}
                    placeholder={null}
                  />
                )}
              </div>

              <div className="flex justify-end gap-3 mt-8">
                <button
                  className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-100 transition-all text-sm"
                  onClick={() => {
                    setShowModal(false);
                    setModalContent(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className={`px-6 py-2.5 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-all text-sm shadow-md shadow-indigo-100 flex items-center gap-2 ${modalLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={modalLoading}
                  onClick={sendNotification}
                >
                  {modalLoading ? (
                    <>
                      <Loader size="xs" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <BsSend className="text-lg" />
                      Send
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Alerts;